// src/pages/admin/Orders.jsx - COMPLETE MOBILE RESPONSIVE FIXED
import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/adminApi';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Menu,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Grid,
  Card,
  CardContent,
  Divider,
  Pagination,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
} from '@mui/material';
import {
  Search,
  MoreVert,
  Visibility,
  LocalShipping,
  CheckCircle,
  Cancel,
  Pending,
  Refresh,
  Download,
  ShoppingBag,
  Person,
  Phone,
  Email,
  LocationOn,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';

const Orders = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // ============================================================
  // STATE
  // ============================================================
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });

  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const limit = isMobile ? 10 : 20;

  // Stats state
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    processing: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0,
  });

  // ============================================================
  // FETCH ORDERS
  // ============================================================
  const fetchOrders = async (currentPage = page) => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.getOrders({
        status: filterStatus !== 'all' ? filterStatus : undefined,
        search: searchTerm || undefined,
        page: currentPage,
        limit: limit,
      });
      
      const data = response.data;
      setOrders(data.orders || []);
      setTotalPages(data.pages || 1);
      setTotalOrders(data.total || 0);
      setPage(currentPage);

      const orderList = data.orders || [];
      setStats({
        total: data.total || orderList.length,
        pending: orderList.filter(o => o.status === 'pending').length,
        processing: orderList.filter(o => o.status === 'processing').length,
        shipped: orderList.filter(o => o.status === 'shipped').length,
        delivered: orderList.filter(o => o.status === 'delivered').length,
        cancelled: orderList.filter(o => o.status === 'cancelled').length,
      });
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to load orders. Please try again.');
      showSnackbar('Failed to load orders', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // EFFECTS
  // ============================================================
  useEffect(() => {
    fetchOrders(1);
  }, [filterStatus]);

  // ============================================================
  // SNACKBAR
  // ============================================================
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // ============================================================
  // HANDLERS
  // ============================================================
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value === '' || value.length > 2) {
      fetchOrders(1);
    }
  };

  const handleFilterChange = (status) => {
    setFilterStatus(status);
  };

  const handlePageChange = (event, value) => {
    fetchOrders(value);
  };

  const handleMenuOpen = (event, order) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrder(order);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewDetails = () => {
    setOpenDetails(true);
    handleMenuClose();
  };

  const handleExpandOrder = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  // ============================================================
  // UPDATE ORDER STATUS
  // ============================================================
  const handleStatusUpdate = async (status) => {
    if (!selectedOrder) {
      showSnackbar('No order selected', 'error');
      return;
    }

    if (selectedOrder.status === status) {
      showSnackbar(`Order is already ${status}`, 'info');
      handleMenuClose();
      return;
    }

    try {
      const response = await adminApi.updateOrderStatus(
        selectedOrder.id, 
        { status: status }
      );

      if (response.data.success) {
        showSnackbar(`Order status updated to ${status}`, 'success');
        fetchOrders(page);
      } else {
        showSnackbar(response.data.message || 'Failed to update order status', 'error');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      let errorMessage = 'Failed to update order status.';
      if (error.response) {
        errorMessage = error.response.data?.message || errorMessage;
      }
      showSnackbar(errorMessage, 'error');
    } finally {
      handleMenuClose();
    }
  };

  const handleRefresh = () => {
    fetchOrders(1);
    showSnackbar('Orders refreshed', 'info');
  };

  // ============================================================
  // HELPERS
  // ============================================================
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'warning';
      case 'processing': return 'info';
      case 'shipped': return 'primary';
      case 'delivered': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return <Pending />;
      case 'processing': return <Refresh />;
      case 'shipped': return <LocalShipping />;
      case 'delivered': return <CheckCircle />;
      case 'cancelled': return <Cancel />;
      default: return <Pending />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const calculateItemCount = (order) => {
    if (!order.OrderItems || !Array.isArray(order.OrderItems)) return 0;
    return order.OrderItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
  };

  const getAvailableActions = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return ['processing', 'cancelled'];
      case 'processing':
        return ['shipped', 'cancelled'];
      case 'shipped':
        return ['delivered'];
      case 'delivered':
        return [];
      case 'cancelled':
        return [];
      default:
        return [];
    }
  };

  const getActionLabel = (status) => {
    switch (status) {
      case 'processing': return 'Process Order';
      case 'shipped': return 'Mark as Shipped';
      case 'delivered': return 'Mark as Delivered';
      case 'cancelled': return 'Cancel Order';
      default: return status;
    }
  };

  const getActionIcon = (status) => {
    switch (status) {
      case 'processing': return <Refresh fontSize="small" sx={{ color: 'info.main' }} />;
      case 'shipped': return <LocalShipping fontSize="small" sx={{ color: 'primary.main' }} />;
      case 'delivered': return <CheckCircle fontSize="small" sx={{ color: 'success.main' }} />;
      case 'cancelled': return <Cancel fontSize="small" sx={{ color: 'error.main' }} />;
      default: return null;
    }
  };

  // ============================================================
  // RENDER MOBILE CARD
  // ============================================================
  const renderMobileOrderCard = (order) => {
    const isExpanded = expandedOrder === order.id;
    const itemCount = calculateItemCount(order);
    const availableActions = getAvailableActions(order.status);

    return (
      <Card key={order.id} sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            {/* Header Row */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  #{order.orderNumber || order.id}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(order.createdAt)}
                </Typography>
              </Box>
              <Chip
                icon={getStatusIcon(order.status)}
                label={order.status || 'Pending'}
                size="small"
                color={getStatusColor(order.status)}
              />
            </Stack>

            {/* Customer & Vendor */}
            <Stack direction="row" spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                  {order.User?.name?.[0]?.toUpperCase() || 'U'}
                </Avatar>
                <Typography variant="body2">
                  {order.User?.name || 'Unknown'}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {order.Vendor?.storeName || 'Unknown'}
              </Typography>
            </Stack>

            {/* Items & Total */}
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {itemCount} {itemCount === 1 ? 'item' : 'items'}
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="primary">
                ${order.totalAmount?.toFixed(2) || '0.00'}
              </Typography>
            </Stack>

            {/* Actions Row */}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {availableActions.map((action) => (
                <Button
                  key={action}
                  size="small"
                  variant="outlined"
                  startIcon={getActionIcon(action)}
                  onClick={() => {
                    setSelectedOrder(order);
                    handleStatusUpdate(action);
                  }}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '0.7rem',
                  }}
                >
                  {getActionLabel(action)}
                </Button>
              ))}
              <Button
                size="small"
                variant="contained"
                startIcon={<Visibility />}
                onClick={() => {
                  setSelectedOrder(order);
                  setOpenDetails(true);
                }}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '0.7rem',
                }}
              >
                View
              </Button>
              <IconButton 
                size="small" 
                onClick={() => handleExpandOrder(order.id)}
              >
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Stack>

            {/* Expanded Details */}
            <Collapse in={isExpanded}>
              <Box sx={{ pt: 1 }}>
                <Divider sx={{ mb: 1.5 }} />
                
                {/* Customer Details */}
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                  Customer Details
                </Typography>
                <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Person fontSize="small" color="action" />
                    <Typography variant="body2">{order.User?.name || 'Unknown'}</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Email fontSize="small" color="action" />
                    <Typography variant="body2">{order.User?.email || 'N/A'}</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Phone fontSize="small" color="action" />
                    <Typography variant="body2">{order.User?.phone || 'N/A'}</Typography>
                  </Stack>
                  {order.shippingAddress && (
                    <Stack direction="row" alignItems="flex-start" spacing={1}>
                      <LocationOn fontSize="small" color="action" />
                      <Typography variant="body2">{order.shippingAddress}</Typography>
                    </Stack>
                  )}
                </Stack>

                <Divider sx={{ mb: 1.5 }} />

                {/* Order Items */}
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                  Order Items ({itemCount})
                </Typography>
                {order.OrderItems && order.OrderItems.length > 0 ? (
                  order.OrderItems.map((item) => (
                    <Stack 
                      key={item.id} 
                      direction="row" 
                      justifyContent="space-between" 
                      alignItems="center"
                      sx={{ py: 0.5 }}
                    >
                      <Typography variant="body2">
                        {item.Product?.name || 'Unknown'}
                        {item.size && ` (${item.size})`}
                        {item.color && ` - ${item.color}`}
                      </Typography>
                      <Typography variant="body2">
                        {item.quantity} × ${item.price?.toFixed(2) || '0.00'}
                      </Typography>
                    </Stack>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No items found
                  </Typography>
                )}

                <Divider sx={{ my: 1.5 }} />

                {/* Order Summary */}
                <Stack spacing={0.5}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">Subtotal</Typography>
                    <Typography variant="body2">${order.subtotal?.toFixed(2) || '0.00'}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">Shipping</Typography>
                    <Typography variant="body2">${order.shippingCost?.toFixed(2) || '0.00'}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">Tax</Typography>
                    <Typography variant="body2">${order.tax?.toFixed(2) || '0.00'}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body1" fontWeight="bold">Total</Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      ${order.totalAmount?.toFixed(2) || '0.00'}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </Collapse>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  // ============================================================
  // RENDER
  // ============================================================
  if (loading && orders.length === 0 && !error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 8, sm: 10 } }}>
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ============================================================
          HEADER
          ============================================================ */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              Orders
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage platform orders ({totalOrders} total)
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} width={{ xs: '100%', sm: 'auto' }}>
            <Button 
              variant="outlined" 
              startIcon={<Refresh />} 
              onClick={handleRefresh}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
            >
              Refresh
            </Button>
            <Button 
              variant="outlined" 
              startIcon={<Download />}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
            >
              Export
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* ============================================================
          STATS CARDS - Responsive
          ============================================================ */}
      <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: 3 }}>
        <Grid item xs={4} sm={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1, sm: 2 } }}>
              <Typography variant={isMobile ? "h6" : "h4"} fontWeight="bold">
                {stats.total}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4} sm={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1, sm: 2 } }}>
              <Typography variant={isMobile ? "h6" : "h4"} fontWeight="bold" color="warning.main">
                {stats.pending}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Pending
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4} sm={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1, sm: 2 } }}>
              <Typography variant={isMobile ? "h6" : "h4"} fontWeight="bold" color="info.main">
                {stats.processing}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Processing
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4} sm={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1, sm: 2 } }}>
              <Typography variant={isMobile ? "h6" : "h4"} fontWeight="bold" color="primary.main">
                {stats.shipped}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Shipped
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4} sm={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1, sm: 2 } }}>
              <Typography variant={isMobile ? "h6" : "h4"} fontWeight="bold" color="success.main">
                {stats.delivered}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Delivered
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4} sm={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1, sm: 2 } }}>
              <Typography variant={isMobile ? "h6" : "h4"} fontWeight="bold" color="error.main">
                {stats.cancelled}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Cancelled
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ============================================================
          SEARCH & FILTERS - Responsive
          ============================================================ */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 2 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={isMobile ? 1 : 2} 
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <TextField
            placeholder="Search by order or customer..."
            value={searchTerm}
            onChange={handleSearch}
            size="small"
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            <Chip
              label="All"
              onClick={() => handleFilterChange('all')}
              color={filterStatus === 'all' ? 'primary' : 'default'}
              clickable
              size="small"
            />
            <Chip
              label="Pending"
              onClick={() => handleFilterChange('pending')}
              color={filterStatus === 'pending' ? 'warning' : 'default'}
              clickable
              size="small"
            />
            <Chip
              label="Processing"
              onClick={() => handleFilterChange('processing')}
              color={filterStatus === 'processing' ? 'info' : 'default'}
              clickable
              size="small"
            />
            <Chip
              label="Shipped"
              onClick={() => handleFilterChange('shipped')}
              color={filterStatus === 'shipped' ? 'primary' : 'default'}
              clickable
              size="small"
            />
            <Chip
              label="Delivered"
              onClick={() => handleFilterChange('delivered')}
              color={filterStatus === 'delivered' ? 'success' : 'default'}
              clickable
              size="small"
            />
            <Chip
              label="Cancelled"
              onClick={() => handleFilterChange('cancelled')}
              color={filterStatus === 'cancelled' ? 'error' : 'default'}
              clickable
              size="small"
            />
          </Box>
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ============================================================
          ORDERS LIST - Mobile Cards or Desktop Table
          ============================================================ */}
      {isMobile ? (
        // Mobile Card View
        <Box>
          {orders.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
              <ShoppingBag sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography color="text.secondary">No orders found</Typography>
            </Paper>
          ) : (
            orders.map((order) => renderMobileOrderCard(order))
          )}
        </Box>
      ) : (
        // Desktop Table View
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Order</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Customer</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Vendor</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Items</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Total</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Status</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2" fontWeight="bold">Actions</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                      <Stack alignItems="center" spacing={1}>
                        <ShoppingBag sx={{ fontSize: 48, color: 'text.secondary' }} />
                        <Typography color="text.secondary">No orders found</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : (
                  orders.map((order) => {
                    const itemCount = calculateItemCount(order);
                    return (
                      <TableRow key={order.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600">
                            #{order.orderNumber || order.id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(order.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: 12 }}>
                              {order.User?.name?.[0]?.toUpperCase() || 'U'}
                            </Avatar>
                            <Typography variant="body2">{order.User?.name || 'Unknown'}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{order.Vendor?.storeName || 'Unknown'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{itemCount} {itemCount === 1 ? 'item' : 'items'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600" color="primary">
                            ${order.totalAmount?.toFixed(2) || '0.00'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(order.status)}
                            label={order.status || 'Pending'}
                            size="small"
                            color={getStatusColor(order.status)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={(e) => handleMenuOpen(e, order)}>
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ============================================================
          PAGINATION
          ============================================================ */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            size={isMobile ? 'small' : 'medium'}
          />
        </Box>
      )}

      {/* ============================================================
          CONTEXT MENU
          ============================================================ */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>
          <Visibility fontSize="small" sx={{ mr: 1 }} /> View Details
        </MenuItem>
        <Divider />
        {getAvailableActions(selectedOrder?.status).map((action) => (
          <MenuItem 
            key={action} 
            onClick={() => handleStatusUpdate(action)}
            sx={{ 
              color: action === 'cancelled' ? 'error.main' : undefined,
            }}
          >
            {getActionIcon(action)}
            {getActionLabel(action)}
          </MenuItem>
        ))}
        {selectedOrder?.status === 'delivered' && (
          <MenuItem disabled>
            <CheckCircle fontSize="small" sx={{ mr: 1, color: 'success.main' }} /> 
            Order Completed
          </MenuItem>
        )}
        {selectedOrder?.status === 'cancelled' && (
          <MenuItem disabled>
            <Cancel fontSize="small" sx={{ mr: 1, color: 'error.main' }} /> 
            Order Cancelled
          </MenuItem>
        )}
      </Menu>

      {/* ============================================================
          ORDER DETAILS DIALOG - Responsive
          ============================================================ */}
      <Dialog
        open={openDetails}
        onClose={() => setOpenDetails(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, m: { xs: 1, sm: 2 } }
        }}
      >
        <DialogTitle>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            alignItems={{ xs: 'flex-start', sm: 'center' }} 
            justifyContent="space-between"
            spacing={1}
          >
            <Typography variant="h6">
              Order #{selectedOrder?.orderNumber || selectedOrder?.id}
            </Typography>
            <Chip
              icon={getStatusIcon(selectedOrder?.status)}
              label={selectedOrder?.status || 'Pending'}
              size="small"
              color={getStatusColor(selectedOrder?.status)}
            />
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedOrder && (
            <Stack spacing={3}>
              {/* Customer Information */}
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                  <Person fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  Customer Information
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
                    {selectedOrder.User?.name?.[0]?.toUpperCase() || 'U'}
                  </Avatar>
                  <Box>
                    <Typography variant="body1" fontWeight="600">
                      {selectedOrder.User?.name || 'Unknown'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedOrder.User?.email || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedOrder.User?.phone || 'N/A'}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {/* Order Items */}
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                  <ShoppingBag fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  Order Items ({calculateItemCount(selectedOrder)} total)
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrder.OrderItems && selectedOrder.OrderItems.length > 0 ? (
                        selectedOrder.OrderItems.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Avatar variant="rounded" src={item.Product?.thumbnail} sx={{ width: 32, height: 32 }}>
                                  <ShoppingBag />
                                </Avatar>
                                <Box>
                                  <Typography variant="body2">
                                    {item.Product?.name || 'Unknown'}
                                  </Typography>
                                  {item.size && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Size: {item.size}
                                    </Typography>
                                  )}
                                  {item.color && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Color: {item.color}
                                    </Typography>
                                  )}
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell align="right">{item.quantity || 0}</TableCell>
                            <TableCell align="right">${item.price?.toFixed(2) || '0.00'}</TableCell>
                            <TableCell align="right" fontWeight="600">
                              ${(item.price * item.quantity)?.toFixed(2) || '0.00'}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="body2" color="text.secondary">No items found</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* Order Summary */}
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                  <CheckCircle fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  Order Summary
                </Typography>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">Subtotal</Typography>
                    <Typography variant="body2">${selectedOrder.subtotal?.toFixed(2) || '0.00'}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">Shipping</Typography>
                    <Typography variant="body2">${selectedOrder.shippingCost?.toFixed(2) || '0.00'}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">Tax</Typography>
                    <Typography variant="body2">${selectedOrder.tax?.toFixed(2) || '0.00'}</Typography>
                  </Stack>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body1" fontWeight="bold">Total</Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      ${selectedOrder.totalAmount?.toFixed(2) || '0.00'}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>

              {/* Shipping Address */}
              {selectedOrder.shippingAddress && (
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                    <LocalShipping fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                    Shipping Address
                  </Typography>
                  <Typography variant="body2">{selectedOrder.shippingAddress}</Typography>
                </Paper>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setOpenDetails(false)} fullWidth={isMobile}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Orders;